import { MeterAccessibility } from '@enmon/commons/lib/enmon';
import { formatBuildingFloorLabel } from '@/components/MeterRecordSummary';
import { formatDetailDisplayValue } from '@/features/inventorization/detail-field-options';
import { INVENTORIZATION_BLOCK_IDS } from '@/features/inventorization/domain/inventorization';
import type { InventorizationContext } from '@/features/inventorization/domain/inventorization-context';
import { readInventorizationContext } from '@/features/inventorization/domain/inventorization-context';
import {
  formatInventorizationCommodity,
  formatInventorizationPlacement,
  type InventorizationIdentity,
  readInventorizationIdentity,
} from '@/features/inventorization/domain/inventorization-identity';
import {
  FLOORPLAN_PLACEMENT_STATES,
  type InventorizationPosition,
  readInventorizationPosition,
} from '@/features/inventorization/domain/inventorization-position';
import type { InventorizationRegisterReading } from '@/features/inventorization/domain/inventorization-reading';
import { isDefaultInventorizationRegisterLabel } from '@/features/inventorization/domain/inventorization-registers';
import {
  type InventorizationOutput,
  type InventorizationTechnicalDetails,
  isRemoteReadingMountType,
  readInventorizationTechnicalDetails,
  readMountType,
} from '@/features/inventorization/domain/inventorization-technical';
import {
  INVENTORIZE_REVIEW_STATUSES,
  type InventorizeReviewStatus,
} from '@/features/inventorization/domain/review/inventorization-review-status';
import {
  readResolvedInventorizationRegisters,
  resolveInventorizationTextValue,
} from '@/features/inventorization/domain/review/inventorization-value-resolver';
import {
  createInventorizeDetailExtractionPath,
  createInventorizeRegisterExtractionPath,
} from '@/features/inventorization/domain/review/inventorize-extraction-paths';
import {
  INVENTORIZE_CONTEXT_REVIEW_FIELD_CONFIGS,
  INVENTORIZE_IDENTITY_REVIEW_FIELD_CONFIGS,
  INVENTORIZE_LOCATION_TECHNICAL_REVIEW_FIELD_CONFIGS,
  INVENTORIZE_NOTE_REVIEW_FIELD_CONFIG,
  INVENTORIZE_REGISTER_REVIEW_FIELD_CONFIGS,
  INVENTORIZE_REMOTE_READING_REVIEW_FIELD_CONFIGS,
  type InventorizeStaticReviewFieldConfig,
  readInventorizeStaticFieldValue,
} from '@/features/inventorization/domain/review/inventorize-review-field-config';
import {
  createInventorizeOutputReviewFieldId,
  createInventorizeRegisterReviewFieldId,
  createInventorizeTechnicalDetailReviewFieldId,
  INVENTORIZE_PHOTO_REVIEW_FIELD_IDS,
  INVENTORIZE_POSITION_REVIEW_FIELD_IDS,
  INVENTORIZE_REGISTER_REVIEW_FIELD_KEYS,
  INVENTORIZE_TECHNICAL_DETAIL_REVIEW_FIELD_IDS,
  type InventorizeRegisterReviewFieldKey,
} from '@/features/inventorization/domain/review/inventorize-review-field-ids';
import type {
  InventorizeReviewField,
  InventorizeReviewSection,
  ReviewTextFieldInput,
} from '@/features/inventorization/domain/review/inventorize-review-field-types';
import { detailWizardLabel } from '@/features/inventorization/field-wizard-i18n';
import { readBuildingNameLabel } from '@/features/inventorization/placement-options';
import { reviewPhotoCount, reviewRegisterLabel, reviewText } from '@/features/inventorization/review-i18n';
import { type CaptureSession, type LocalPhotoRef, PHOTO_CONTEXTS } from '@/shared/capture/capture-session';
import { translateInventoryText } from '@/shared/i18n/inventory-i18n';
import { getObisOptionsForCommodity } from '@/shared/meters/register-metadata';
import type { AiExtraction } from '@/shared/ocr/ai-extraction';
import type { PartialInventorizationExtraction } from '@/shared/ocr/inventorize-extraction-contract';
import { getLatestCompletedInventorizeExtraction } from '@/shared/ocr/inventorize-extraction-jobs';

export type { InventorizeReviewField, InventorizeReviewSection };
export { INVENTORIZE_REVIEW_STATUSES, type InventorizeReviewStatus };

const OWNER_TYPE_DETAIL_KEY = 'ownerType';
const accessibilityKeyByValue: Record<MeterAccessibility, string> = {
  [MeterAccessibility.Bad]: 'bad',
  [MeterAccessibility.Basement]: 'basement',
  [MeterAccessibility.Ceiling]: 'ceiling',
  [MeterAccessibility.FairlyHigh]: 'fairlyHigh',
  [MeterAccessibility.FloodedShaft]: 'floodedShaft',
  [MeterAccessibility.Good]: 'good',
  [MeterAccessibility.High]: 'high',
  [MeterAccessibility.Shaft]: 'shaft',
  [MeterAccessibility.Tenant]: 'tenant',
  [MeterAccessibility.VeryHigh]: 'veryHigh',
};

interface InventorizeReviewFieldOptions {
  manufacturerDisplayNames?: Record<string, string>;
  meterModelDisplayNames?: Record<string, string>;
}

export function createInventorizeReviewFields(
  session: CaptureSession,
  options: InventorizeReviewFieldOptions = {},
): InventorizeReviewField[] {
  const context = readInventorizationContext(session);
  const identity = readInventorizationIdentity(session);
  const position = readInventorizationPosition(session);
  const technical = readInventorizationTechnicalDetails(session);
  const extraction = getLatestCompletedInventorizeExtraction(session.aiExtractions);
  const registers = readResolvedInventorizationRegisters(session);
  const requiresInventorizePhotos = !session._id;
  const showsRemoteReadingFields = isRemoteReadingMountType(readExtractedMountType(extraction) || technical.mountType);

  return [
    ...createPhotoReviewFields({
      requiresInventorizePhotos,
      session,
      showsRemoteReadingFields,
    }),
    ...createLocationReviewFields({
      aiExtractions: session.aiExtractions,
      context,
      extraction,
      identity,
      position,
      technical,
    }),
    ...createIdentityReviewFields({
      aiExtractions: session.aiExtractions,
      extraction,
      identity,
      manufacturerDisplayNames: options.manufacturerDisplayNames,
      meterModelDisplayNames: options.meterModelDisplayNames,
    }),
    ...createRegisterReviewFields({
      aiExtractions: session.aiExtractions,
      commodity: identity.commodity,
      extraction,
      registers,
    }),
    ...createRemoteReadingReviewFields({
      aiExtractions: session.aiExtractions,
      extraction,
      technical,
    }),
    ...createSignalReviewFields({
      aiExtractions: session.aiExtractions,
      detail: technical.detail,
    }),
    ...createDetailReviewFields({
      aiExtractions: session.aiExtractions,
      detail: technical.detail,
      extraction,
      identity,
    }),
    ...createStaticReviewFields([INVENTORIZE_NOTE_REVIEW_FIELD_CONFIG], {
      aiExtractions: session.aiExtractions,
      context,
      extraction,
      identity,
      meterModelDisplayNames: options.meterModelDisplayNames,
      technical,
    }),
  ];
}

export function summarizeInventorizeReviewFields(fields: InventorizeReviewField[]) {
  const summary: Record<InventorizeReviewStatus, number> = {
    [INVENTORIZE_REVIEW_STATUSES.accepted]: 0,
    [INVENTORIZE_REVIEW_STATUSES.empty]: 0,
    [INVENTORIZE_REVIEW_STATUSES.manual]: 0,
    [INVENTORIZE_REVIEW_STATUSES.parsed]: 0,
    [INVENTORIZE_REVIEW_STATUSES.unsure]: 0,
    [INVENTORIZE_REVIEW_STATUSES.waived]: 0,
  };

  for (const field of fields) {
    summary[field.status] += 1;
  }

  return summary;
}

export function getNextInventorizeReviewIssue(fields: InventorizeReviewField[]) {
  return fields.find(field => field.required && field.status === INVENTORIZE_REVIEW_STATUSES.empty);
}

export function countBlockingInventorizeReviewFields(fields: InventorizeReviewField[]) {
  return fields.filter(field => field.required && field.status === INVENTORIZE_REVIEW_STATUSES.empty).length;
}

function createPhotoReviewFields(input: {
  requiresInventorizePhotos: boolean;
  session: CaptureSession;
  showsRemoteReadingFields: boolean;
}): InventorizeReviewField[] {
  const newPhotos = input.session.photos.filter(photo => !photo.uploadedRemoteId);

  return [
    createPhotoReviewField({
      id: INVENTORIZE_PHOTO_REVIEW_FIELD_IDS.context,
      label: reviewText('fields.contextPhotos', 'Context photos'),
      photos: newPhotos.filter(photo => photo.context === PHOTO_CONTEXTS.context),
      required: input.requiresInventorizePhotos,
      targetBlockId: INVENTORIZATION_BLOCK_IDS.context,
    }),
    createPhotoReviewField({
      id: INVENTORIZE_PHOTO_REVIEW_FIELD_IDS.detail,
      label: reviewText('fields.detailPhotos', 'Detail photos'),
      photos: newPhotos.filter(photo => photo.context === PHOTO_CONTEXTS.detail),
      required: input.requiresInventorizePhotos,
      targetBlockId: INVENTORIZATION_BLOCK_IDS.technical,
    }),
    createPhotoReviewField({
      id: INVENTORIZE_PHOTO_REVIEW_FIELD_IDS.registerMain,
      label: reviewText('fields.registerCounterPhotos', 'Counter photos for registers to create'),
      photos: newPhotos.filter(photo => photo.context === PHOTO_CONTEXTS.register),
      required: input.requiresInventorizePhotos,
      section: 'Registers',
      targetBlockId: INVENTORIZATION_BLOCK_IDS.readings,
    }),
    ...(input.showsRemoteReadingFields
      ? [
          createPhotoReviewField({
            id: INVENTORIZE_PHOTO_REVIEW_FIELD_IDS.module,
            label: reviewText('fields.modulePhotos', 'Module photos'),
            photos: newPhotos.filter(photo => photo.context === PHOTO_CONTEXTS.module),
            required: true,
            section: 'Remote reading',
            targetBlockId: INVENTORIZATION_BLOCK_IDS.technical,
          }),
        ]
      : []),
  ];
}

function createLocationReviewFields({
  aiExtractions,
  context,
  extraction,
  identity,
  position,
  technical,
}: {
  aiExtractions: AiExtraction[];
  context: InventorizationContext;
  extraction: PartialInventorizationExtraction | undefined;
  identity: InventorizationIdentity;
  position: InventorizationPosition;
  technical: InventorizationTechnicalDetails;
}) {
  return [
    ...createStaticReviewFields(
      [
        INVENTORIZE_CONTEXT_REVIEW_FIELD_CONFIGS[0],
        INVENTORIZE_CONTEXT_REVIEW_FIELD_CONFIGS[1],
        INVENTORIZE_LOCATION_TECHNICAL_REVIEW_FIELD_CONFIGS[0],
      ],
      {
        aiExtractions,
        context,
        extraction,
        identity,
        technical,
      },
    ),
    createReviewTextField({
      aiExtractions,
      id: createInventorizeTechnicalDetailReviewFieldId(OWNER_TYPE_DETAIL_KEY),
      label: reviewText('fields.ownerType', 'Owner type'),
      required: true,
      section: 'Location',
      targetBlockId: INVENTORIZATION_BLOCK_IDS.technical,
      value: technical.detail[OWNER_TYPE_DETAIL_KEY] ?? '',
      displayValue: formatOwnerTypeDisplayValue(technical.detail[OWNER_TYPE_DETAIL_KEY]),
    }),
    ...createStaticReviewFields(
      [INVENTORIZE_CONTEXT_REVIEW_FIELD_CONFIGS[2], INVENTORIZE_CONTEXT_REVIEW_FIELD_CONFIGS[3]],
      {
        aiExtractions,
        context,
        extraction,
        identity,
        technical,
      },
    ),
    createReviewTextField({
      aiExtractions,
      id: INVENTORIZE_POSITION_REVIEW_FIELD_IDS.gps,
      label: reviewText('fields.gpsPosition', 'GPS position'),
      required: false,
      section: 'Location',
      targetBlockId: INVENTORIZATION_BLOCK_IDS.position,
      value:
        position.gpsLat !== null && position.gpsLng !== null
          ? `${formatCoordinate(position.gpsLat)}, ${formatCoordinate(position.gpsLng)}`
          : position.gpsUnavailableReason
            ? reviewText('values.unavailableWithReason', 'Unavailable: {reason}', {
                reason: position.gpsUnavailableReason,
              })
            : '',
    }),
    createReviewTextField({
      aiExtractions,
      id: INVENTORIZE_POSITION_REVIEW_FIELD_IDS.floorplan,
      label: reviewText('fields.floorplanMarker', 'Floorplan marker'),
      required: false,
      section: 'Location',
      targetBlockId: INVENTORIZATION_BLOCK_IDS.position,
      value:
        position.floorplanLat !== null && position.floorplanLng !== null
          ? `${formatCoordinate(position.floorplanLat)}, ${formatCoordinate(position.floorplanLng)}`
          : position.floorplanNotPlacedReason
            ? reviewText('values.notPlacedWithReason', 'Not placed: {reason}', {
                reason: position.floorplanNotPlacedReason,
              })
            : position.floorplanState === FLOORPLAN_PLACEMENT_STATES.notAvailable
              ? reviewText('values.noFloorplanAvailable', 'No floorplan available')
              : '',
    }),
    ...createStaticReviewFields([INVENTORIZE_LOCATION_TECHNICAL_REVIEW_FIELD_CONFIGS[1]], {
      aiExtractions,
      context,
      extraction,
      identity,
      technical,
    }),
  ];
}

function createIdentityReviewFields(input: {
  aiExtractions: AiExtraction[];
  extraction?: PartialInventorizationExtraction;
  identity: InventorizationIdentity;
  manufacturerDisplayNames?: Record<string, string>;
  meterModelDisplayNames?: Record<string, string>;
}): InventorizeReviewField[] {
  return createStaticReviewFields(INVENTORIZE_IDENTITY_REVIEW_FIELD_CONFIGS, {
    aiExtractions: input.aiExtractions,
    extraction: input.extraction,
    identity: input.identity,
    manufacturerDisplayNames: input.manufacturerDisplayNames,
    meterModelDisplayNames: input.meterModelDisplayNames,
  });
}

function createRegisterReviewFields(input: {
  aiExtractions: AiExtraction[];
  commodity: InventorizationIdentity['commodity'];
  extraction?: PartialInventorizationExtraction;
  registers: InventorizationRegisterReading[];
}) {
  return input.registers.flatMap((register, index) => {
    const label = register.label || reviewRegisterLabel(index);

    return INVENTORIZE_REGISTER_REVIEW_FIELD_CONFIGS.map(config =>
      createReviewTextField({
        aiExtractions: input.aiExtractions,
        allowedValues:
          config.field === INVENTORIZE_REGISTER_REVIEW_FIELD_KEYS.obis
            ? getObisOptionsForCommodity(input.commodity).map(option => option.value)
            : undefined,
        candidateField: index === 0 ? config.candidateFieldForFirstRegister : undefined,
        extraction: input.extraction,
        extractionPath: config.extractionKey
          ? createInventorizeRegisterExtractionPath(index, config.extractionKey)
          : undefined,
        id: createInventorizeRegisterReviewFieldId(register.id, config.field),
        label: reviewText(config.labelKey, config.labelFallback, { register: label }),
        required: config.required,
        section: 'Registers',
        source: register.photoRef,
        targetBlockId: INVENTORIZATION_BLOCK_IDS.readings,
        value: readRegisterReviewValue(register, index, config.field),
      }),
    );
  });
}

function createRemoteReadingReviewFields(input: {
  aiExtractions: AiExtraction[];
  extraction?: PartialInventorizationExtraction;
  technical: InventorizationTechnicalDetails;
}): InventorizeReviewField[] {
  const [mountTypeConfig, ...remoteReadingConfigs] = INVENTORIZE_REMOTE_READING_REVIEW_FIELD_CONFIGS;
  const remoteMounted = isRemoteReadingMountType(readExtractedMountType(input.extraction) || input.technical.mountType);
  const staticInput = {
    aiExtractions: input.aiExtractions,
    extraction: input.extraction,
    remoteMounted,
    technical: input.technical,
  };

  return [
    ...createStaticReviewFields([mountTypeConfig], staticInput),
    ...createOutputReviewFields(input.technical.outputs),
    ...createStaticReviewFields(remoteReadingConfigs, staticInput),
  ];
}

function createSignalReviewFields(input: { aiExtractions: AiExtraction[]; detail: Record<string, string> }) {
  return [
    createReviewTextField({
      aiExtractions: input.aiExtractions,
      displayValue: formatRssiDisplayValue(input.detail.lorawanRssi),
      id: INVENTORIZE_TECHNICAL_DETAIL_REVIEW_FIELD_IDS.lorawanRssi,
      label: reviewText('fields.lorawanRssi', 'LoRaWAN RSSI'),
      required: false,
      section: 'Remote reading',
      targetBlockId: INVENTORIZATION_BLOCK_IDS.technical,
      value: input.detail.lorawanRssi ?? '',
    }),
    createReviewTextField({
      aiExtractions: input.aiExtractions,
      displayValue: formatRssiDisplayValue(input.detail.nbiotRssi),
      id: INVENTORIZE_TECHNICAL_DETAIL_REVIEW_FIELD_IDS.nbiotRssi,
      label: reviewText('fields.nbiotRssi', 'NB-IoT RSSI'),
      required: false,
      section: 'Remote reading',
      targetBlockId: INVENTORIZATION_BLOCK_IDS.technical,
      value: input.detail.nbiotRssi ?? '',
    }),
  ];
}

function createDetailReviewFields(input: {
  aiExtractions: AiExtraction[];
  detail: Record<string, string>;
  extraction?: PartialInventorizationExtraction;
  identity: InventorizationIdentity;
}) {
  const extractedDetail =
    input.extraction?.detail && typeof input.extraction.detail === 'object' && !Array.isArray(input.extraction.detail)
      ? input.extraction.detail
      : {};
  const keys = createDetailReviewKeys(input.detail, extractedDetail);

  return [...keys].toSorted(compareDetailKeys).flatMap(key =>
    isExplicitSignalDetailKey(key)
      ? []
      : [
          createReviewTextField({
            aiExtractions: input.aiExtractions,
            extraction: input.extraction,
            extractionPath: createInventorizeDetailExtractionPath(key),
            id: createInventorizeTechnicalDetailReviewFieldId(key),
            label: detailWizardLabel(key),
            required: false,
            section: 'Meter details',
            targetBlockId: INVENTORIZATION_BLOCK_IDS.technical,
            value: readDetailReviewValue(input.detail, key),
            displayValue: readDetailReviewDisplayValue(
              input.detail,
              key,
              input.identity,
              readDetailReviewValue(input.detail, key),
            ),
            formatDisplayValue: value => readDetailReviewDisplayValue(input.detail, key, input.identity, value),
          }),
        ],
  );
}

function createPhotoReviewField(input: {
  id: string;
  label: string;
  photos: LocalPhotoRef[];
  required: boolean;
  section?: InventorizeReviewSection;
  targetBlockId: (typeof INVENTORIZATION_BLOCK_IDS)[keyof typeof INVENTORIZATION_BLOCK_IDS];
}): InventorizeReviewField {
  return {
    id: input.id,
    label: input.label,
    required: input.required,
    section: input.section ?? 'Photos',
    status: input.photos.length > 0 ? INVENTORIZE_REVIEW_STATUSES.manual : INVENTORIZE_REVIEW_STATUSES.empty,
    targetBlockId: input.targetBlockId,
    value: input.photos.length > 0 ? reviewPhotoCount(input.photos.length) : '',
  };
}

function createStaticReviewFields(
  configs: readonly InventorizeStaticReviewFieldConfig[],
  input: {
    aiExtractions: AiExtraction[];
    context?: InventorizationContext;
    extraction?: PartialInventorizationExtraction;
    identity?: InventorizationIdentity;
    manufacturerDisplayNames?: Record<string, string>;
    meterModelDisplayNames?: Record<string, string>;
    remoteMounted?: boolean;
    technical?: InventorizationTechnicalDetails;
  },
): InventorizeReviewField[] {
  return configs.flatMap(config => {
    if (config.knownMeterOnly && input.identity?.newMeter) {
      return [];
    }

    if (
      config.remoteMountedOnly &&
      !(input.remoteMounted ?? (input.technical && isRemoteReadingMountType(input.technical.mountType)))
    ) {
      return [];
    }

    const value = readInventorizeStaticFieldValue(config, input);

    return [
      createReviewTextField({
        aiExtractions: input.aiExtractions,
        candidateField: config.candidateField,
        extraction: input.extraction,
        extractionPath: config.extractionPath,
        formatDisplayValue: resolvedValue => readStaticReviewDisplayValue(config, input, resolvedValue),
        id: config.id,
        label: reviewText(config.labelKey, config.labelFallback),
        required: config.required,
        section: config.section,
        targetBlockId: config.targetBlockId,
        value,
      }),
    ];
  });
}

function createReviewTextField(input: ReviewTextFieldInput): InventorizeReviewField {
  const resolvedValue = resolveInventorizationTextValue(input);
  const displayValue =
    input.formatDisplayValue?.(resolvedValue.value) ??
    (resolvedValue.value === input.value ? input.displayValue : undefined);

  return {
    confidence: resolvedValue.confidence,
    id: input.id,
    label: input.label,
    required: input.required,
    section: input.section,
    source: resolvedValue.source,
    sourceKind: resolvedValue.sourceKind,
    status: resolvedValue.status,
    targetBlockId: input.targetBlockId,
    value: resolvedValue.value,
    displayValue,
  };
}

function readStaticReviewDisplayValue(
  config: InventorizeStaticReviewFieldConfig,
  input: {
    context?: InventorizationContext;
    identity?: InventorizationIdentity;
    manufacturerDisplayNames?: Record<string, string>;
    meterModelDisplayNames?: Record<string, string>;
    technical?: InventorizationTechnicalDetails;
  },
  value: string,
) {
  if (config.scope === 'context' && config.key === 'buildingName') {
    return readBuildingNameLabel(value);
  }

  if (config.scope === 'context' && config.key === 'floorName') {
    return formatBuildingFloorLabel(value);
  }

  if (config.scope === 'technical' && config.key === 'mountType') {
    return formatRemoteReadingMountTypeDisplayValue(value);
  }

  if (config.scope === 'technical' && config.key === 'accessibility') {
    return formatAccessibilityDisplayValue(input.technical?.accessibility ?? value);
  }

  if (config.scope !== 'identity') {
    return undefined;
  }

  switch (config.key) {
    case 'commodity':
      return formatInventorizationCommodity(input.identity?.commodity ?? '');
    case 'placement':
      return input.identity ? formatInventorizationPlacement(input.identity.placement) : undefined;
    case 'newMeter':
      return value === 'true' ? reviewText('values.yes', 'Yes') : reviewText('values.no', 'No');
    case 'manufacturer':
      return input.manufacturerDisplayNames?.[value];
    case 'meterModel':
      return input.meterModelDisplayNames?.[value];
    default:
      return undefined;
  }
}

function readExtractedMountType(extraction?: PartialInventorizationExtraction) {
  return readMountType(extraction?.meter_mount_type);
}

function readRegisterReviewValue(
  register: InventorizationRegisterReading,
  index: number,
  field: InventorizeRegisterReviewFieldKey,
) {
  if (field === 'label') {
    return isDefaultInventorizationRegisterLabel(register.label, index) ? '' : register.label;
  }

  return register[field];
}

function createOutputReviewFields(outputs: InventorizationOutput[]): InventorizeReviewField[] {
  return outputs.map((output, index) => {
    const owner = output.remoteReadingOwner.trim();
    const isOwnerMissing = output.isOccupied && owner.length === 0;

    return {
      id: createInventorizeOutputReviewFieldId(index),
      label: reviewText('fields.output', '{technology} output', { technology: output.technology }),
      required: output.isOccupied,
      section: 'Remote reading',
      status: isOwnerMissing ? INVENTORIZE_REVIEW_STATUSES.empty : INVENTORIZE_REVIEW_STATUSES.manual,
      targetBlockId: INVENTORIZATION_BLOCK_IDS.technical,
      value: output.isOccupied
        ? owner
          ? reviewText('values.occupiedBy', 'Occupied - {owner}', { owner })
          : reviewText('values.occupied', 'Occupied')
        : reviewText('values.free', 'Free'),
    };
  });
}

function createDetailReviewKeys(detail: Record<string, string>, extractedDetail: Record<string, unknown>) {
  const keys = new Set([...Object.keys(detail), ...Object.keys(extractedDetail)]);

  if (keys.has('width.value') || keys.has('width.unit')) {
    keys.add('width');
    keys.delete('width.value');
    keys.delete('width.unit');
  }

  return keys;
}

function readDetailReviewValue(detail: Record<string, string>, key: string) {
  return key === 'width' ? (detail['width.value'] ?? detail.width ?? '') : (detail[key] ?? '');
}

function readDetailReviewDisplayValue(
  detail: Record<string, string>,
  key: string,
  identity: InventorizationIdentity,
  value = readDetailReviewValue(detail, key),
) {
  if (key === 'layLength') {
    return value ? `${value} mm` : undefined;
  }

  const formatted = formatDetailDisplayValue(key, value, identity.commodity);

  if (formatted) {
    return formatted;
  }

  if (key !== 'width') {
    return undefined;
  }

  const widthValue = detail['width.value'] ?? detail.width ?? '';
  const widthUnit = detail['width.unit'];

  return widthValue && widthUnit ? `${widthValue} ${widthUnit}` : undefined;
}

function compareDetailKeys(left: string, right: string) {
  const leftRank = getDetailKeyRank(left);
  const rightRank = getDetailKeyRank(right);

  if (leftRank !== rightRank) {
    return leftRank - rightRank;
  }

  return left.localeCompare(right);
}

function getDetailKeyRank(key: string) {
  switch (key) {
    case 'phases':
      return 0;
    case 'circuitBreakerSize':
      return 1;
    default:
      return 2;
  }
}

function formatRssiDisplayValue(value: unknown) {
  const text =
    typeof value === 'number' && Number.isFinite(value) ? String(value) : typeof value === 'string' ? value.trim() : '';

  if (!text) {
    return undefined;
  }

  const numberValue = Number(text);

  if (!Number.isFinite(numberValue)) {
    return text;
  }

  return `${numberValue === 0 ? 0 : -Math.abs(numberValue)} dBm`;
}

function isExplicitSignalDetailKey(key: string) {
  return key === 'lorawanRssi' || key === 'nbiotRssi' || key === OWNER_TYPE_DETAIL_KEY;
}

function formatRemoteReadingMountTypeDisplayValue(value: unknown) {
  const text = typeof value === 'string' ? value.trim() : '';

  return text ? translateInventoryText(`app.common.inventoryMeterMountType.${text}`, text) : undefined;
}

function formatOwnerTypeDisplayValue(value: unknown) {
  const text = typeof value === 'string' ? value.trim() : '';

  return text ? translateInventoryText(`app.common.meterOwnerType.${text}`, text) : undefined;
}

function formatAccessibilityDisplayValue(value: unknown) {
  const values = Array.isArray(value) ? value : (typeof value === 'string' ? value.trim() : '').split(',');
  const labels = values
    .map(item => (typeof item === 'string' ? item.trim() : ''))
    .filter((item): item is string => Boolean(item))
    .map(formatAccessibilityValue);

  return labels.length > 0 ? labels.join(', ') : undefined;
}

function formatAccessibilityValue(value: string) {
  const key = accessibilityKeyByValue[value as MeterAccessibility];

  return key ? translateInventoryText(`app.inventory.accessibility.${key}`, value) : value;
}

function formatCoordinate(value: number) {
  return value.toFixed(6);
}
